'use client';

import { usePathname } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { useBreakpoint } from '@/shared/hooks/use-media-query';
import { AccountSubpageNav } from '@/features/settings/ui/AccountSubpageNav';
import { MobileAccountHeader } from '@/features/settings/ui/MobileAccountHeader';
import {
  allAccountLinkItems,
  navigableAccountItems,
} from '@/features/settings/model/account-menu';
import { AppSidebar } from '@/features/project';
import { useSidebarStore } from '@/shared/stores/sidebar-store';
import { BackButton, DesktopShell, contentColumnEndPadding } from '@/shared/ui';
import { AssistantLauncherSlot } from '@/shared/assistant';
import { FeedbackHeaderButton } from '@/features/support/ui/FeedbackHeaderButton';

export default function AccountLayout({
  children,
}: {
  children: React.ReactNode;
}) {
  const { isLg } = useBreakpoint();
  const sidebarCollapsed = useSidebarStore((s) => s.collapsed);
  const pathname = usePathname();

  const showSubpageNav = navigableAccountItems.some(
    (item) => pathname === item.href || pathname.startsWith(item.href + '/')
  );
  const currentMenuItem = allAccountLinkItems.find(
    (item) => pathname === item.href || pathname.startsWith(item.href + '/')
  );

  // Account is a single-pane, push-navigation surface on every width: /account
  // shows the menu list, /account/<sub> shows a detail with a back button.
  // There is no side-by-side settings-list + detail column — the user sees
  // EITHER the menu OR a detail, mirroring the Apple/Android single-column
  // layout. The content is centered and capped (`max-w-[760px]`) so that on a
  // wide window — with the app sidebar collapsed — it occupies roughly the
  // reading width it has with the sidebar shown instead of stretching
  // edge-to-edge (parity with the Help page, also capped at 760).
  //
  // One wrapper tree at every width, as in ProjectDesktopLayout: below `lg`
  // the wrappers collapse to `display: contents` (DesktopShell `bare`). The
  // breakpoint resolves after hydration, and swapping trees remounted every
  // account page once, so state read on mount (the sign-in methods' one-shot
  // merge message) was taken by the first copy and lost.
  return (
    <DesktopShell sidebar={<AppSidebar />} bare={!isLg}>
      {!isLg && <MobileAccountHeader />}
      {/* Collapsed RIGHT padding mirrors DesktopShell's LEFT gutter so the
          capped `max-w-[760px]` content re-centers, with the scrollbar staying
          at the window edge (padding is inside the border box). The collapsed
          state ALSO surfaces the floating bottom bar (MobileNavigation, fixed
          bottom-3), so add a bottom clearance (pb-24) on the scroll container —
          otherwise the last cards scroll under the bar and can never be fully
          revealed. */}
      <main
        className={
          isLg ? `flex-1 min-w-0 overflow-y-auto${sidebarCollapsed ? ' pb-24' : ''}` : 'contents'
        }
        style={isLg ? contentColumnEndPadding(sidebarCollapsed ? 56 : 0) : undefined}
      >
        <div className={isLg ? 'mx-auto w-full max-w-[760px]' : 'contents'}>
          {isLg && currentMenuItem && (
            <AccountSubpageHeader
              labelKey={currentMenuItem.labelKey}
              showNav={showSubpageNav}
              showBack
            />
          )}
          {children}
        </div>
      </main>
    </DesktopShell>
  );
}

/**
 * Sticky account-page header. Renders the page title (sourced from the same
 * sidebar label key, so header and sidebar stay in sync) on the left and the
 * prev/next chevron pager on the right — mirrors Apple's `navigationTitle`
 * + chevron toolbar layout.
 *
 * `showBack` enables a leading back-chevron that returns to the /account
 * menu list. It is only used in the compact-desktop layout where the
 * settings aside is hidden — wide layout has the aside as the navigation
 * affordance and doesn't need a back button.
 */
function AccountSubpageHeader({
  labelKey,
  showNav,
  showBack,
}: {
  labelKey: string;
  showNav: boolean;
  showBack: boolean;
}) {
  const t = useTranslations('account');
  return (
    <div className="sticky top-0 z-10 flex items-center justify-between gap-2 h-14 px-2 sm:px-4 lg:px-6 backdrop-blur-xl">
      <div className="flex items-center gap-1 min-w-0 flex-1">
        {showBack && (
          <BackButton href="/account" size="compact" label={t('title')} />
        )}
        <h1 className="text-page-title text-foreground truncate">
          {t(labelKey)}
        </h1>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <AssistantLauncherSlot placement="toolbar" />
        <FeedbackHeaderButton />
        {showNav && <AccountSubpageNav />}
      </div>
    </div>
  );
}
